import type { Dispatch } from 'redux'

import { basketPromoCodeChange } from 'actions/basket'
import { promoGet, promoChange } from 'actions/promos'
import { redirect } from 'actions/redirect'
import { trackUTMAndPromoCode } from 'actions/tracking'
import type { Promo } from 'selectors/promoStoreSelectors'

/**
 * empty = nothing entered
 * success = promo code is valid
 * error = promo code is not valid
 */
export type PromoCodeCheckStatus = 'empty' | 'success' | 'error'

/**
 * Given the promoStore entry, return the human-readable campaign description as html.
 *
 * The legacy promo code entering process (in gousto-admin) adds inline style attributes that
 * don't match the current design; safeHtml strips them when the text is rendered.
 */
export const getPromoCodeCampaignTextHtml = (promoStoreEntry?: Promo) =>
  promoStoreEntry?.codeData?.campaign?.modalText || null

/**
 * Given a promo code typed by the user, set the correct state to display the
 * page based on the cache of requests in promoStore, or starting the request
 * to retrieve the promo code information if necessary.
 */
export const checkPromoCode = (
  promoCode: string,
  isPending: boolean,
  dispatch: Dispatch<any>,
  promoStore: {
    [key: string]: Promo
  },
  setStatus: (status: PromoCodeCheckStatus) => void,
  setCampaignTextHtml: (campaignTextHtml: string) => void,
) => {
  if (!promoCode) {
    setStatus('empty')

    return
  }
  if (isPending) {
    return
  }

  const promoStoreEntry = promoStore[promoCode]
  if (!promoStoreEntry) {
    dispatch(promoGet(promoCode))

    return
  }

  const hasError = !!promoStoreEntry?.errorText
  if (!hasError) {
    const campaignTextHtml = getPromoCodeCampaignTextHtml(promoStoreEntry)
    setCampaignTextHtml(campaignTextHtml || '')
  }
  setStatus(hasError ? 'error' : 'success')
}

/**
 * Apply the given promo code, send a snowplow event, and leave to the next
 * page in the flow.
 */
export const proceedWithPromoCode = (
  dispatch: Dispatch<any>,
  promoCode: string,
  eventType: string,
  eventAdditionalData = {},
) => {
  dispatch(promoChange(promoCode))
  dispatch(basketPromoCodeChange(promoCode))
  dispatch(trackUTMAndPromoCode(eventType, eventAdditionalData))
  dispatch(redirect('/signup'))
}
